import React, { useEffect } from 'react';
import Layout from '../layouts/Layout';
import { PUBLIC_URL } from '../utils/const';
import { BlogLanguageWrapper, BlogPage } from 'reactyll';
import MarkDown from 'react-markdown'
import rehypeRaw from 'rehype-raw';
import { Head } from 'vite-react-ssg';
import { Container, Title, Text, Image, Box, Paper, Anchor, Group, Button, Stack, Divider } from '@mantine/core';
import { redirectWithParameters } from '../utils/common';
import { getBlogByLanguage } from '../utils/blog';
import { blogs } from '../_blog/routes';
import { useTranslation } from 'react-i18next';
import MoreFromOurBlog from '../components/MoreFromOurBlog';
import { trackCtaClick } from '../utils/analytics';

export interface ExtraProperties {
    date: string
    headline: string
    description: string
    publishDate: string
    readMore?: string[]
}

const content: BlogPage<ExtraProperties> = JSON.parse(decodeURIComponent(atob(`JTdCJTIybWFya2Rvd24lMjIlM0ElMjIlNUNuUXVlc3RhJTIwJUMzJUE4JTIwdW5hJTIwZ3VpZGElMjBzZW1wbGljZSUyMHBlciUyMHNpbmNyb25penphcmUlMjBwaSVDMyVCOSUyMGNhbGVuZGFyaSUyMEdvb2dsZSUyMGluJTIwdW5vJTJDJTIwaW5pemlhbW8hJTVDbiU1Q24lMjMlMjMlMjBQYXNzbyUyMDElM0ElMjBSZWdpc3RyYXRpJTIwc3UlMjBDYWxlbnN5bmMubGl2ZSU1Q25Ob24lMjBwcmVvY2N1cGFydGklMkMlMjBjJyVDMyVBOCUyMHVuYSUyMHByb3ZhJTIwZ3JhdHVpdGElMjBzZSUyMG5vbiUyMHNlaSUyMHNvZGRpc2ZhdHRvLiUyMENsaWNjYSUyMHNlbXBsaWNlbWVudGUlMjBpbCUyMHB1bHNhbnRlJTIwcXVpJTIwc290dG8lMjBlJTIwdG9ybmElMjBhJTIwcXVlc3RhJTIwcGFnaW5hJTIwZG9wby4lNUNuJTVDbiU1QkNVU1RPTV9CVVRUT04lM0FSZWdpc3RyYXRpJTVEKGh0dHBzJTNBJTJGJTJGY2FsZW5zeW5jLmxpdmUlMkZsb2dpbiUzRnNpZ251cCUzRHRydWUpJTVDbiU1Q24lMjMlMjMlMjBQYXNzbyUyMDIlM0ElMjBBZ2dpdW5naSUyMGlsJTIwdHVvJTIwYWNjb3VudCUyMChvJTIwaSUyMHR1b2klMjBhY2NvdW50KSUyMEdvb2dsZSU1Q24lNUNuU2UlMjBpJTIwdHVvaSUyMGNhbGVuZGFyaSUyMHNvbm8lMjB0dXR0aSUyMG5lbGxvJTIwc3Rlc3NvJTIwYWNjb3VudCUyQyUyMGRvdnJhaSUyMGNvbm5ldHRlcmUlMjBzb2xvJTIwcXVlbGxvLiUyMEFsdHJpbWVudGklMkMlMjBjb25uZXR0aSUyMHR1dHRpJTIwcXVlbGxpJTIwbmVjZXNzYXJpLiU1Q24lNUNuJTIzJTIzJTIwUGFzc28lMjAzJTNBJTIwQ3JlYSUyMGxlJTIwdHVlJTIwcmVnb2xlJTIwZGklMjBzaW5jcm9uaXp6YXppb25lJTIwR29vZ2xlJTVDbiU1Q25JbiUyMHF1ZXN0byUyMGFjY291bnQlMjBobyUyMDMlMjBjYWxlbmRhcmklMkMlMjAqdGVzdColMkMlMjAqdGVzdDIqJTIwZSUyMCp0ZXN0MyouJTIwVm9nbGlvJTIwc2luY3Jvbml6emFyZSUyMCp0ZXN0MiolMjBlJTIwKnRlc3QzKiUyMGluJTIwdGVzdC4lNUNuRGFsbGElMjBkYXNoYm9hcmQlMkMlMjBjbGljY2ElMjBpbCUyMHB1bHNhbnRlJTIwJTVDJTIyQWdnaXVuZ2klMjBzaW5jcm9uaXp6YXppb25lJTVDJTIyLiU1Q24lNUNuJTIzJTIzJTIzJTIwUGFzc28lMjAzLjElM0ElMjBDcmVhJTIwbGElMjBzaW5jcm9uaXp6YXppb25lJTIwZGVsJTIwY2FsZW5kYXJpbyUyMEdvb2dsZSU1Q25JbXBvc3RhJTIwaWwlMjBjYWxlbmRhcmlvJTIwc29yZ2VudGUlMjBzdSUyMCp0ZXN0MyolMjBlJTIwaWwlMjBjYWxlbmRhcmlvJTIwZGklMjBkZXN0aW5hemlvbmUlMjBzdSUyMCp0ZXN0Ki4lMjBTZSUyMHZ1b2klMjB1bmElMjBzaW5jcm9uaXp6YXppb25lJTIwbm9ybWFsZSUyQyUyMHNlbnphJTIwY2FtYmlhcmUlMjBpbCUyMHRpdG9sbyUyMG8lMjBpbCUyMGNvbG9yZSUyQyUyMHF1ZXN0byUyMCVDMyVBOCUyMHR1dHRvJTIwY2klQzMlQjIlMjBkaSUyMGN1aSUyMGhhaSUyMGJpc29nbm8uJTIwQ2xpY2NhJTIwJTVDJTIyU2FsdmElNUMlMjIlMjBlJTIwaW4lMjBwb2NoaSUyMG1pbnV0aSUyMHZlZHJhaSUyMHR1dHRpJTIwaSUyMGNhbGVuZGFyaSUyMHNpbmNyb25penphdGkuJTVDbiU1Q24lM0NpbWclMjBzcmMlM0QlNUMlMjIlMkZhc3NldHMlMkZibG9nJTJGc3luYy1tdWx0aXBsZS1nb29nbGUtY2FsZW5kYXJzJTJGc3luY2hyb25pemUtdHdvLWdvb2dsZS1jYWxlbmRhcnMucG5nJTVDJTIyJTIwc3R5bGUlM0QlNUMlMjJtYXgtaGVpZ2h0JTNBNjAwcHglM0IlMjB3aWR0aCUzQWF1dG8lM0IlNUMlMjIlMjBhbHQlM0Qnc3luY2hyb25pemUlMjB0d28lMjBnb29nbGUlMjBjYWxlbmRhcnMlMjB0b2dldGhlciclM0UlNUNuJTVDbiUyMyUyMyUyMyUyMFBhc3NvJTIwMy4yJTNBJTIwUmlwZXRpJTIwcGVyJTIwb2duaSUyMGFsdHJhJTIwc2luY3Jvbml6emF6aW9uZSU1Q25JbiUyMHF1ZXN0byUyMGVzZW1waW8lMkMlMjByaXBldGVyJUMzJUIyJTIwbG8lMjBzdGVzc28lMjBwcm9jZXNzbyUyMG1hJTIwcXVlc3RhJTIwdm9sdGElMjBpbCUyMGNhbGVuZGFyaW8lMjBzb3JnZW50ZSUyMHNhciVDMyVBMCUyMCp0ZXN0MiouJTVDbiU1Q24lMjMlMjMlMjAlQzMlODglMjB0dXR0byU1Q25UaSUyMGF2ZXZhbW8lMjBkZXR0byUyMGNoZSUyMGNpJTIwc2FyZWJiZSUyMHZvbHV0byUyMHNvbG8lMjB1biUyMG1pbnV0byElMjBEb3ZyZXN0aSUyMHZlZGVyZSUyMGFwcGFyaXJlJTIwaSUyMHR1b2klMjBldmVudGklMjBzaW5jcm9uaXp6YXRpJTIwZSUyMGQnb3JhJTIwaW4lMjBwb2klMjBvZ25pJTIwdm9sdGElMjBjaGUlMjB1biUyMGV2ZW50byUyMHZpZW5lJTIwY3JlYXRvJTIwbyUyMG1vZGlmaWNhdG8lMjBpbiUyMCp0ZXN0MiolMjBvJTIwKnRlc3QzKiUyQyUyMHZlcnIlQzMlQTAlMjBhdXRvbWF0aWNhbWVudGUlMjBzaW5jcm9uaXp6YXRvLiU1Q24lNUNuQnVvbmElMjBzaW5jcm9uaXp6YXppb25lISU1Q24lNUNuJTIyJTJDJTIycHJvcGVydGllcyUyMiUzQSU3QiUyMnRpdGxlJTIyJTNBJTIyQ29tZSUyMHNpbmNyb25penphcmUlMjBwaSVDMyVCOSUyMGNhbGVuZGFyaSUyMEdvb2dsZSUyMGluc2llbWUlMjIlMkMlMjJ1cmwlMjIlM0ElMjIlMkZibG9nJTJGc2luY3Jvbml6emFyZS1waXUtY2FsZW5kYXJpLWdvb2dsZS1pbnNpZW1lJTIyJTJDJTIybGFuZ3VhZ2UlMjIlM0ElMjJpdCUyMiUyQyUyMmRhdGUlMjIlM0ElMjJBcHJpbCUyMDIwMjUlMjIlMkMlMjJwdWJsaXNoRGF0ZSUyMiUzQSUyMjIwMjUtMDQtMTclMjIlMkMlMjJoZWFkbGluZSUyMiUzQSUyMkltcGFyYSUyMGElMjBzaW5jcm9uaXp6YXJlJTIwcGklQzMlQjklMjBjYWxlbmRhcmklMjBHb29nbGUlMjBpbiUyMHVubyUyMGluJTIwMSUyMG1pbnV0byUyMiUyQyUyMmRlc2NyaXB0aW9uJTIyJTNBJTIySW1wYXJhJTIwYSUyMHNpbmNyb25penphcmUlMjBwaSVDMyVCOSUyMGNhbGVuZGFyaSUyMEdvb2dsZSUyMGluJTIwdW5vJTIwaW4lMjAxJTIwbWludXRvJTIyJTdEJTJDJTIybGFuZ3VhZ2VzJTIyJTNBJTVCJTVCJTIyZW4lMjIlMkMlMjIlMkZibG9nJTJGc3luYy1tdWx0aXBsZS1nb29nbGUtY2FsZW5kYXJzLWludG8tb25lJTIyJTVEJTJDJTVCJTIyZnIlMjIlMkMlMjIlMkZibG9nJTJGc3luY2hyb25pc2VyLXBsdXNpZXVycy1jYWxlbmRyaWVycy1nb29nbGUtZW4tdW4tc2V1bCUyMiU1RCUyQyU1QiUyMml0JTIyJTJDJTIyJTJGYmxvZyUyRnNpbmNyb25penphcmUtcGl1LWNhbGVuZGFyaS1nb29nbGUtaW5zaWVtZSUyMiU1RCUyQyU1QiUyMmVzJTIyJTJDJTIyJTJGYmxvZyUyRnNpbmNyb25pemFyLW11bHRpcGxlcy1jYWxlbmRhcmlvcy1Hb29nbGUtZW4tdW5vJTIyJTVEJTVEJTdE`)));


const InnerBlogTemplate: React.FC = () => {
    const { i18n } = useTranslation();

    const matchingLanguageCode = content.properties.language || i18n.resolvedLanguage;    
    
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get('lang');

        if (langParam) {
            const matchingLanguage = content.languages.find(([lang, url]) => lang === langParam);
            if (matchingLanguage) {
                const [_, url] = matchingLanguage;
                window.location.href = `${url}`;
            }
        }
        

        const handleButtonClick = (event: any) => {
            if (event.target.classList.contains('cta')) {
                trackCtaClick('blog', 'inline');
                window.location.href = "/login?signup=true"
            }
        };

        const parentElement = document.getElementById('blog-wrapper');
        if (parentElement) {
            document.addEventListener('click', handleButtonClick);
        }

        return () => {
            if (parentElement) {
                parentElement.removeEventListener('click', handleButtonClick);
            }
        };
    }, []);

    return (
        <Box bg="gray.0">
            <Head>
                <meta charSet="utf-8" />
                <title>{content.properties.title}</title>
                <link rel="canonical" href={`https://calensync.live${content.properties.url}`} />
                {content.languages.map(([language, url]) => (
                    <link rel="alternate" key={language} href={`https://calensync.live${url}`} hrefLang={language} />
                ))}
                <meta name="description" content={content.properties.description} />
                <meta property="og:title" content={content.properties.title} />
                <meta property="og:url" content={`https://calensync.live${content.properties.url}`} />
                <meta property="og:description" content={content.properties.description} />
                <meta property="og:publish_date" content={content.properties.publishDate} />
            </Head>
            <Container size="md" mt="md" id="blog-wrapper">
                <Paper p="md" my="md" bg="gray.0">
                    <Box mb="md" mt="md">
                        <Title order={1} mb="xs">{content.properties.title}</Title>
                        <Text fs="italic" c="dimmed">{content.properties.date}</Text>
                    </Box>

                    <Box mb="xl">
                        <MarkDown
                            rehypePlugins={[rehypeRaw]}
                            children={content.markdown}
                            components={{
                                h1(props) {
                                    const { children } = props
                                    return <Title order={1}>{children}</Title>
                                },
                                p(props) {
                                    const { children } = props
                                    return <Text size="lg" mb="md">
                                        {children}
                                    </Text>
                                },
                                strong(props) {
                                    const { children } = props
                                    return <Text span fw={700}><b>{children}</b></Text>
                                },
                                h2(props) {
                                    const { children } = props
                                    return <Title fw={400} order={2} mt="xl" pt="md" pb="md">{children}</Title>
                                },
                                h3(props) {
                                    const { children } = props
                                    return <Title fz="xl" fw={300} order={3} mt="md" pt="sm" pb="sm">{children}</Title>
                                },
                                img(props) {
                                    const { src, alt, style, width } = props
                                    return <Image
                                        mx="auto"
                                        my="sm"
                                        src={src}
                                        alt={alt}
                                        style={style}
                                        w={width}
                                        radius="md"
                                    />
                                },
                                a(props) {
                                    const { children, href } = props
                                    if (typeof children === 'string' && children.includes('CUSTOM_BUTTON')) {
                                        const content = children.replace('CUSTOM_BUTTON:', '')
                                        const onClick = () => {
                                            trackCtaClick('blog', 'button');
                                            redirectWithParameters('login', { signup: 'true' }, true)
                                        }
                                        return (
                                            <Group justify='center' my='md'>
                                                <Stack justify='center' my='md' gap="0">
                                                    <Button color="primary.4" size="compact-md" className="cta" onClick={onClick}>{content}</Button>
                                                    <Text size="sm" c="dimmed">.. and get 7 days free trial</Text>
                                                </Stack>
                                            </Group>
                                        )
                                    } else if (href.toLowerCase().startsWith('blog/')) {
                                        const blogId = href.toLowerCase().replace('blog/', '')
                                        const content = getBlogByLanguage(blogs[blogId], matchingLanguageCode)
                                        return <Anchor href={content.url}>{children}</Anchor>
                                    } else {
                                        return <Anchor href={href}>{children}</Anchor>
                                    }
                                }
                            }}
                        />
                    </Box>
                    {content.properties.readMore && (
                        <MoreFromOurBlog 
                            blogs={content.properties.readMore.map((blogId) => blogs[blogId])} 
                            matchingLanguageCode={matchingLanguageCode} 
                        />
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

const BlogTemplate: React.FC = () => {
    return (
        <Layout>
            <InnerBlogTemplate />
        </Layout>
    );
};

export default BlogTemplate;